/**
 * O jardim no painel — o `daily_bloom.dart`.
 *
 * A planta de quem entrou, a chuva da estação por trás dela, as duas contas
 * (sequência e visitas) e os dois botões: trocar a planta e ir para o jardim
 * dos dois. É aqui que a visita do dia é registrada, como no `initState` do
 * `dashboard_screen.dart`.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COLECOES,
  PLANTAS,
  comAVisitaDeHoje,
  faseEfetiva,
  jardimDeMapa,
  jardimParaMapa,
  ligacao,
  naEstacao,
  plantaPorId,
  traduzir,
  usarDocumento,
  usarEstadoDoArmazem,
  usarSessao,
} from '@nosso-sempre/shared';
import { Folha } from './Folha';
import { Icone } from './Icone';
import { Planta } from './Planta';
import './jardimCartao.css';

/**
 * Lá o painel vive dentro de uma `ShellRoute` e o `initState` dele roda uma vez
 * por sessão do app. Aqui o painel é desmontado a cada troca de aba, e sem esta
 * trava a mesma pessoa marcaria uma visita a cada vez que voltasse para casa —
 * a planta cresceria por andar pelo app, e não por aparecer.
 */
let visitaRegistrada = false;

export function CartaoDoJardim() {
  const { armazem } = ligacao();
  const { quem } = usarSessao();
  const { pronto } = usarEstadoDoArmazem();
  const doc = usarDocumento(COLECOES.jardim, quem ?? '');
  const cantero = doc ? jardimDeMapa(doc) : null;

  const [escolhendo, setEscolhendo] = useState(false);
  const [dica, setDica] = useState(false);
  const [pulso, setPulso] = useState(false);

  useEffect(() => {
    // Só depois que o armazém chegou: marcar a visita antes disso apagaria a
    // sequência de quem já vinha regando, porque o que está gravado ainda não
    // está aqui.
    if (!quem || !pronto || visitaRegistrada) return;
    visitaRegistrada = true;
    const atual = armazem.documento(COLECOES.jardim, quem);
    armazem
      .gravar(
        COLECOES.jardim,
        quem,
        jardimParaMapa(comAVisitaDeHoje(atual ? jardimDeMapa(atual) : null)),
      )
      .catch(() => {
        // Uma visita perdida não vale um aviso na cara de ninguém: a planta
        // cresce na próxima.
      });
  }, [armazem, quem, pronto]);

  // A dica some sozinha depois de três segundos, como o `Future.delayed` de lá.
  const relogio = useRef<number>();
  useEffect(() => {
    if (!dica) return;
    relogio.current = window.setTimeout(() => setDica(false), 3000);
    return () => window.clearTimeout(relogio.current);
  }, [dica]);

  useEffect(() => {
    if (!pulso) return;
    const t = window.setTimeout(() => setPulso(false), 200);
    return () => window.clearTimeout(t);
  }, [pulso]);

  const especie = plantaPorId(cantero?.plantType ?? 'lily');
  const fase = faseEfetiva(especie, cantero?.currentStage ?? 0);

  async function trocarPlanta(id: string) {
    if (!quem) return;
    setEscolhendo(false);
    const atual = armazem.documento(COLECOES.jardim, quem);
    try {
      if (atual) {
        // Só o tipo muda: o resto do jardim continua onde estava.
        await armazem.gravar(COLECOES.jardim, quem, { plantType: id }, true);
      } else {
        await armazem.gravar(
          COLECOES.jardim,
          quem,
          jardimParaMapa({
            currentStage: 0,
            lastVisit: new Date(),
            streakCount: 0,
            totalInteractions: 0,
            plantType: id,
          }),
        );
      }
    } catch {
      // Igual à visita: quem escolheu a planta tenta de novo, e nada se perde.
    }
  }

  return (
    <div className="jc">
      <div className="jc-palco">
        <Clima />

        {dica ? (
          <div className="jc-dica" role="status">
            <p className="jc-dica-fase">{especie.stageDescriptions[fase]}</p>
            {naEstacao(especie) ? (
              <p className="jc-dica-estacao">
                <Icone nome="brilho" tamanho={12} />
                {especie.seasonalBonusName} bonus active
              </p>
            ) : null}
          </div>
        ) : null}

        <button
          className={pulso ? 'jc-toque pulsando' : 'jc-toque'}
          type="button"
          onClick={() => {
            setDica((d) => !d);
            setPulso(true);
          }}
          aria-label={traduzir('Sua {v}, fase {fase} de 5. Toque para ver.', {
            v: especie.displayName.toLowerCase(),
            fase,
          })}
        >
          <span aria-hidden="true">
            <Planta especie={especie} fase={fase} tamanho={180} />
          </span>
        </button>
      </div>

      {cantero ? (
        <div className="jc-contas">
          <p className="jc-linha">
            <span className="jc-selo">
              <Icone nome="chama" tamanho={14} />
              {cantero.streakCount} day streak
            </span>
            <span className="jc-selo">
              <Icone nome="coracao" tamanho={14} />
              {cantero.totalInteractions} visits
            </span>
          </p>
          <p className="jc-linha">
            <button className="jc-acao" type="button" onClick={() => setEscolhendo(true)}>
              <Icone nome="trocar" tamanho={14} />
              {traduzir('Trocar a planta')}
            </button>
            <Link className="jc-acao" to="/garden">
              <Icone nome="flor" tamanho={14} />
              {traduzir('Nosso jardim')}
            </Link>
          </p>
        </div>
      ) : null}

      <Folha
        aberta={escolhendo}
        aoFechar={() => setEscolhendo(false)}
        titulo={traduzir('Escolha sua planta')}
        recado={traduzir('Cada planta tem um mês de bônus na estação dela')}
      >
        <ul className="jc-plantas">
          {PLANTAS.map((p) => {
            const escolhida = p.id === (cantero?.plantType ?? 'lily');
            return (
              <li key={p.id}>
                <button
                  className={escolhida ? 'jc-planta escolhida' : 'jc-planta'}
                  type="button"
                  onClick={() => void trocarPlanta(p.id)}
                  aria-pressed={escolhida}
                >
                  <span className="jc-planta-emoji" aria-hidden="true">
                    {p.emoji}
                  </span>
                  <span className="jc-planta-nomes">
                    <span className="jc-planta-nome">
                      {p.displayName}
                      {naEstacao(p) ? (
                        <span className="jc-planta-agora">
                          <Icone nome="brilho" tamanho={10} />
                          {p.seasonalBonusName}
                        </span>
                      ) : null}
                    </span>
                    {p.seasonalBonusName ? (
                      <span className="jc-planta-bonus">Bonus: {p.seasonalBonusName}</span>
                    ) : null}
                  </span>
                  {escolhida ? <Icone nome="certo-circulo" tamanho={20} /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </Folha>
    </div>
  );
}

/**
 * O `garden_weather_overlay.dart`: quinze partículas caindo, com a forma da
 * estação. O mês manda, e não uma previsão do tempo — é o `currentSeason()` de
 * lá, inverno/primavera/verão/outono pelo hemisfério norte, como no Flutter.
 */
function Clima() {
  const tela = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = tela.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mes = new Date().getMonth() + 1;
    const estacao =
      mes >= 3 && mes <= 5 ? 1 : mes >= 6 && mes <= 8 ? 2 : mes >= 9 && mes <= 11 ? 3 : 0;

    const particulas = Array.from({ length: 15 }, () => ({
      x: Math.random(),
      y: -Math.random(), // Começa acima da moldura.
      speed: 0.002 + Math.random() * 0.003,
      size: 2 + Math.random() * 4,
      opacity: 0.2 + Math.random() * 0.4,
      drift: (Math.random() - 0.5) * 0.001,
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.02,
    }));

    let pedido = 0;
    const escala = window.devicePixelRatio || 1;

    function quadro() {
      const { clientWidth: largura, clientHeight: altura } = canvas!;
      if (canvas!.width !== Math.round(largura * escala)) {
        canvas!.width = Math.round(largura * escala);
        canvas!.height = Math.round(altura * escala);
      }
      ctx!.setTransform(escala, 0, 0, escala, 0, 0);
      ctx!.clearRect(0, 0, largura, altura);

      for (const p of particulas) {
        p.y += p.speed;
        p.x += p.drift;
        p.rotation += p.rotSpeed;
        if (p.y > 1.2) {
          p.y = -0.1;
          p.x = Math.random();
        }

        const dx = p.x * largura;
        const dy = p.y * altura;

        if (estacao === 0) {
          // Inverno: neve.
          ctx!.fillStyle = `rgba(255, 255, 255, ${p.opacity * 0.5})`;
          ctx!.beginPath();
          ctx!.arc(dx, dy, p.size, 0, Math.PI * 2);
          ctx!.fill();
        } else if (estacao === 2) {
          // Verão: poeira de sol.
          ctx!.fillStyle = `rgba(255, 213, 79, ${p.opacity * 0.3})`;
          ctx!.beginPath();
          ctx!.arc(dx, dy, p.size * 0.8, 0, Math.PI * 2);
          ctx!.fill();
        } else {
          ctx!.save();
          ctx!.translate(dx, dy);
          ctx!.rotate(p.rotation);
          ctx!.beginPath();
          if (estacao === 1) {
            // Primavera: pétalas caindo.
            ctx!.fillStyle = `rgba(248, 187, 208, ${p.opacity})`;
            ctx!.moveTo(0, 0);
            ctx!.quadraticCurveTo(p.size, -p.size * 0.8, 0, -p.size * 1.5);
            ctx!.quadraticCurveTo(-p.size, -p.size * 0.8, 0, 0);
          } else {
            // Outono: folhas douradas.
            ctx!.fillStyle = `rgba(255, 183, 77, ${p.opacity})`;
            ctx!.moveTo(0, -p.size);
            ctx!.quadraticCurveTo(p.size, -p.size * 0.5, 0, p.size);
            ctx!.quadraticCurveTo(-p.size, -p.size * 0.5, 0, -p.size);
          }
          ctx!.fill();
          ctx!.restore();
        }
      }

      pedido = requestAnimationFrame(quadro);
    }

    pedido = requestAnimationFrame(quadro);
    return () => cancelAnimationFrame(pedido);
  }, []);

  return <canvas ref={tela} className="jc-clima" aria-hidden="true" />;
}
